import React, { Component, type ReactNode } from 'react'
import styled from 'styled-components'
import { colors, PrimaryButton, GrayButton } from '../theme/sharedComponents'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 2rem;
  text-align: center;
`

const Icon = styled.div`
  font-size: 3rem;
  color: ${colors.danger};
  margin-bottom: 1.25rem;
`

const Title = styled.h2`
  color: ${colors.textLightMode};
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`

const Message = styled.p`
  color: ${colors.textLightModeMuted};
  font-size: 0.95rem;
  max-width: 420px;
  margin-bottom: 0.5rem;
`

const ErrorId = styled.p`
  color: ${colors.textLightModeMuted};
  font-size: 0.75rem;
  margin-bottom: 1.5rem;
`

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: center;
`

const SupportBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${colors.textLightModeMuted};
  font-size: 0.85rem;
  cursor: pointer;
  text-decoration: underline;
  font-family: inherit;
  margin-top: 0.75rem;

  &:hover { color: ${colors.primary}; }
`

interface Props {
  children: ReactNode
  onSupportClick?: () => void
  dashboardHref?: string
}

interface State {
  hasError: boolean
  errorId: string | null
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, errorId: null }
  }

  static getDerivedStateFromError(): State {
    const errorId = Math.random().toString(36).slice(2, 8).toUpperCase()
    return { hasError: true, errorId }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('ErrorBoundary caught:', error, info.componentStack)
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, errorId: null })
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Container>
          <Icon><i className="bi bi-exclamation-triangle" /></Icon>
          <Title>Something went wrong</Title>
          <Message>
            An unexpected error occurred. Try again or go back to your dashboard.
            If the problem persists, contact support.
          </Message>
          {this.state.errorId && (
            <ErrorId>Reference: #{this.state.errorId}</ErrorId>
          )}
          <Actions>
            <PrimaryButton onClick={this.handleRetry}>
              <i className="bi bi-arrow-clockwise" /> Try Again
            </PrimaryButton>
            {this.props.dashboardHref && (
              <GrayButton as="a" href={this.props.dashboardHref}>
                Go to Dashboard
              </GrayButton>
            )}
          </Actions>
          {this.props.onSupportClick && (
            <SupportBtn onClick={this.props.onSupportClick}>
              <i className="bi bi-headset me-1" />Contact Support
            </SupportBtn>
          )}
        </Container>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary

// Wrapper that injects useSupport() into the class-based ErrorBoundary
import { useSupport } from '../context/SupportModalContext'

interface ErrorBoundaryWithSupportProps {
  children: ReactNode
  dashboardHref?: string
}

export function ErrorBoundaryWithSupport({ children, dashboardHref }: ErrorBoundaryWithSupportProps) {
  const { openSupport } = useSupport()
  return (
    <ErrorBoundary onSupportClick={openSupport} dashboardHref={dashboardHref}>
      {children}
    </ErrorBoundary>
  )
}
